import androidx.compose.runtime.*
import androidx.compose.runtime.snapshots.SnapshotStateMap
import kotlinx.browser.window
import kotlinx.browser.document
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.coroutines.suspendCancellableCoroutine
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.Element
import org.w3c.dom.events.MouseEvent
import org.w3c.files.File
import org.w3c.files.FileReader
import org.w3c.files.FileList
import org.w3c.files.get
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import kotlin.js.Promise

/** Unknown marks stay unresolved, never silently becoming misses. */
fun parsePhotoFrame(raw: String, tenth: Boolean): List<Int>? {
    val marks = raw.uppercase().filterNot { it.isWhitespace() }
    if (marks.isEmpty() || marks.any { it !in "0123456789X/-" }) return null
    val throws = mutableListOf<Int>()
    for (mark in marks) {
        val prior = throws.lastOrNull()
        val sameRack = throws.size == 1 && throws[0] != 10 ||
            tenth && throws.size == 2 && throws[0] == 10 && throws[1] != 10
        val pins = when (mark) {
            'X' -> if (sameRack) return null else 10
            '/' -> if (sameRack && prior != null) 10 - prior else return null
            '-' -> 0
            else -> mark.digitToInt()
        }
        if (sameRack && prior!! + pins > 10) return null
        throws.add(pins)
    }
    val expected = if (!tenth) { if (throws[0] == 10) 1 else 2 }
        else if (throws[0] == 10 || throws.take(2).sum() == 10) 3 else 2
    return throws.takeIf { it.size == expected }
}

fun photoFramesError(type: String, frames: List<FrameDto>): String? {
    if (frames.size != 10) return "Zápis musí mít 10 framů."
    frames.forEachIndexed { i, frame ->
        val t = frame.t
        if (t.isEmpty() || t.any { it !in 0..10 }) return "Zkontroluj frame ${i + 1}."
        val strike = Scoring.isStrike(type, t[0])
        val spare = !strike && t.size >= 2 && Scoring.isSpare(type, t[0], t[1])
        val count = if (i == 9 && (strike || spare)) 3 else if (strike) 1 else 2
        if (t.size != count) return "Neúplný nebo neplatný frame ${i + 1}."
        if (!strike && t[0] + t[1] > 10) return "Součet hodů ve framu ${i + 1} je větší než 10."
        if (i == 9 && strike && !Scoring.isStrike(type, t[1]) && t[1] + t[2] > 10) return "Neplatné bonusové hody."
    }
    return null
}

private suspend fun photoData(file: File): String = suspendCancellableCoroutine { cont ->
    val reader = FileReader()
    reader.onload = { if (cont.isActive) cont.resume(reader.result as String) }
    reader.onerror = { if (cont.isActive) cont.resumeWithException(IllegalStateException("Fotku nelze přečíst.")) }
    cont.invokeOnCancellation { reader.abort() }
    reader.readAsDataURL(file)
}

private data class PhotoSource(val id: Int, val name: String, val data: String, val number: Int)
private data class PhotoPoint(val x: Double, val y: Double)
private data class PhotoDraft(val sourceId: Int, val position: Int, val revision: Int,
    val name: String, val preview: String, val frames: List<FrameDto>,
    val raw: List<String>, val player: String, val number: Int, val selected: Boolean = true,
    val expected: String = "", val playerOverride: Boolean = false)

internal fun nextUnprocessedPhoto(current: Int, sourceIds: List<Int>, processed: Set<Int>): Int? =
    sourceIds.indices.firstOrNull { it > current && sourceIds[it] !in processed }
        ?: sourceIds.indices.firstOrNull { it < current && sourceIds[it] !in processed }

internal fun isHeicPhoto(name: String, type: String): Boolean = when (type.lowercase()) {
    "image/heic", "image/heif", "image/heic-sequence", "image/heif-sequence" -> true
    "image/jpeg", "image/png", "image/webp" -> false // Some browsers convert HEIC while retaining its filename.
    else -> name.substringAfterLast('.', "").lowercase() in setOf("heic", "heif")
}

internal fun isSupportedPhoto(name: String, type: String): Boolean =
    type.lowercase() in setOf("image/jpeg", "image/png", "image/webp") || isHeicPhoto(name, type)

@Composable
fun PhotoImport(
    profiles: List<SgProfileDto>,
    sheets: SnapshotStateMap<String, List<FrameDto>>,
    scores: SnapshotStateMap<String, String>? = null,
    typeForGame: (Int) -> String = { "classic" },
    onGames: (Int) -> Unit = {},
    allowedGames: Map<String, Int?>? = null,
    training: Boolean = false,
    onSelectGame: ((List<FrameDto>, Int?) -> Unit)? = null,
) {
    val singleGame = onSelectGame != null
    var open by remember { mutableStateOf(false) }
    var sources by remember { mutableStateOf<List<PhotoSource>>(emptyList()) }
    var sourceIndex by remember { mutableStateOf(0) }
    var points by remember { mutableStateOf<List<PhotoPoint>>(emptyList()) }
    var drafts by remember { mutableStateOf<List<PhotoDraft>>(emptyList()) }
    val positionPlayers = remember { mutableStateMapOf<Int, String>() }
    val photoRows = remember { mutableStateMapOf<Int, Int>() }
    var defaultRows by remember { mutableStateOf(if (training || singleGame) 2 else 1) }
    var nextSourceId by remember { mutableStateOf(0) }
    var nextRevision by remember { mutableStateOf(0) }
    var reviewSourceId by remember { mutableStateOf<Int?>(null) }
    var busy by remember { mutableStateOf(false) }
    var progress by remember { mutableStateOf("") }
    var error by remember { mutableStateOf<String?>(null) }
    var confirmed by remember { mutableStateOf(false) }
    var detecting by remember { mutableStateOf(false) }
    var detectionAttempt by remember { mutableStateOf(0) }
    var selectionNote by remember { mutableStateOf("") }
    var uploadInput by remember { mutableStateOf<HTMLInputElement?>(null) }
    var dragging by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()
    fun loadFiles(selected: List<File>) {
        if (busy || selected.isEmpty()) return
        open = true; busy = true; error = null
        scope.launch {
            try {
                require(selected.size <= 12) { "Najednou vyber nejvýše 12 fotek." }
                require(!singleGame || selected.size == 1) { "Vyber jednu fotku pro tuto hru." }
                require(selected.all { it.size.toDouble() <= 8 * 1024 * 1024 && isSupportedPhoto(it.name, it.type) }) { "Podporovány jsou JPG, PNG, WebP a HEIC/HEIF do 8 MB." }
                val firstGame = (drafts.maxOfOrNull { it.number } ?: 0) + 1
                val loaded = selected.mapIndexed { i, file ->
                    val data = if (isHeicPhoto(file.name, file.type)) {
                        progress = "Převádím HEIC/HEIF: ${file.name}…"
                        try { (window.asDynamic().SgPhotoOcr.convertHeic(file) as Promise<String>).await() }
                        catch (e: kotlinx.coroutines.CancellationException) { throw e }
                        catch (e: Throwable) { throw IllegalStateException("${file.name}: Převod HEIC/HEIF selhal. ${e.message ?: "Zkus fotku exportovat jako JPG."}") }
                    } else photoData(file)
                    PhotoSource(nextSourceId++, file.name, data, firstGame + i)
                }
                if (singleGame) drafts = emptyList()
                sources = loaded; sourceIndex = 0; points = emptyList()
            } catch (e: kotlinx.coroutines.CancellationException) { throw e }
            catch (e: Throwable) { error = e.message }
            finally { busy = false; progress = "" }
        }
    }
    fun chooseFiles() {
        uploadInput?.let { input -> input.value = ""; input.click() }
    }
    Input(type = InputType.File) {
        attr("hidden", "hidden"); attr("aria-label", if (singleGame) "Fotka výsledkové tabule" else "Fotky výsledkové tabule")
        attr("accept", "image/jpeg,image/png,image/webp,image/heic,image/heif,image/heic-sequence,image/heif-sequence,.heic,.heif"); if (!singleGame) attr("multiple", "multiple")
        if (busy) disabled()
        ref { input -> uploadInput = input; onDispose { uploadInput = null } }
        onChange { ev ->
            val files = (ev.nativeEvent.target as? HTMLInputElement)?.files
            loadFiles((0 until (files?.length ?: 0)).mapNotNull { files?.get(it) })
        }
    }
    LaunchedEffect(nextRevision) {
        if (nextRevision > 0 && reviewSourceId != null) {
            document.querySelector(".photo-game[data-source-id='$reviewSourceId']")?.asDynamic()
                ?.scrollIntoView(js("({block:'start',behavior:'smooth'})"))
        }
    }
    Button(attrs = { classes("small-btn"); if (busy) disabled(); onClick { open = true; chooseFiles() } }) { Text(if (singleGame) "Načíst zápis z fotky (OCR)" else "Načíst hry z fotek (OCR)") }
    if (!open) return
    Div(attrs = { classes("sheet-overlay") }) {
        Div(attrs = { classes("sheet-panel", "photo-import") }) {
            H3 { Text(if (singleGame) "Vyber zápis pro otevřenou hru" else "Import her z fotek") }
            P { Text("OCR běží na tomto zařízení. Při prvním použití se stáhne rozpoznávací knihovna; fotky se nikam neodesílají.") }
            if (singleGame) P { Text("Načti fotku se 2–3 zápisy a vyber správný řádek. Hráč i číslo hry zůstávají podle otevřeného dialogu.") }
            if (training) {
                H4 { Text("Pořadí hráčů v sérii fotek") }
                P(attrs = { classes("muted") }) { Text("Přiřaď hráče podle pozice shora jen jednou. Stejné přiřazení se použije na všech fotkách této série. U jednotlivých her ho můžeš změnit.") }
                Div(attrs = { classes("training-meta", "photo-position-players") }) {
                    for (position in 1..3) Label {
                        Text("$position. hráč shora")
                        Select(attrs = {
                            classes("field"); attr("data-position", "$position")
                            onChange { ev ->
                                val player = ev.value ?: ""
                                positionPlayers[position] = player
                                drafts = drafts.map { d -> if (d.position == position && !d.playerOverride) d.copy(player = player) else d }
                                confirmed = false
                            }
                        }) {
                            Option("", attrs = { if (positionPlayers[position].isNullOrEmpty()) selected() }) { Text("— vyber hráče —") }
                            profiles.forEach { p -> Option(p.blPlayerId, attrs = { if (positionPlayers[position] == p.blPlayerId) selected() }) { Text(p.displayName) } }
                        }
                    }
                }
            }
            val chosen = drafts.filter { it.selected }
            val keys = chosen.map { "${it.player}#${it.number}" }
            val duplicate = keys.distinct().size != keys.size
            val invalid = chosen.any { d -> profiles.none { it.blPlayerId == d.player } || d.number !in 1..999 ||
                (allowedGames != null && "${d.player}#${d.number}" !in allowedGames) || photoFramesError(typeForGame(d.number), d.frames) != null ||
                (d.expected.isNotBlank() && d.expected.toIntOrNull() !in 0..300) }
            drafts.forEach { d ->
                val type = typeForGame(d.number)
                val total = Scoring.total(type, d.frames.map { it.t })
                fun change(next: PhotoDraft) { drafts = drafts.map { if (it.sourceId == d.sourceId && it.position == d.position) next else it }; confirmed = false }
                key(d.sourceId, d.position, d.revision, type) {
                Div(attrs = { classes("photo-game"); attr("data-position", "${d.position}"); attr("data-source-id", "${d.sourceId}") }) {
                    if (singleGame) H4 { Text("${d.position}. zápis shora") }
                    else Label { Input(type = InputType.Checkbox) { checked(d.selected); onChange { change(d.copy(selected = it.value)) } }; Text("Hra ${d.number} · ${d.position}. hráč shora · ${d.name}") }
                    Img(src = d.preview, alt = "Vyrovnaný výřez hry", attrs = { classes("photo-row-preview") })
                    if (singleGame) {
                        ScoresheetFields(type, d.frames, onChange = { frames, _ -> change(d.copy(frames = frames)) }, highlightIncomplete = true)
                        P { Text("Součet hodů: $total · Skóre na fotce: ${d.expected.ifBlank { "nečitelné" }}") }
                        photoFramesError(type, d.frames)?.let { P { Text(it) } }
                        if (d.expected.isNotBlank() && d.expected.toIntOrNull() != total) P { Text("Součet hodů nesouhlasí se skóre na fotce. Oprav hody v políčkách výše.") }
                        Button(attrs = { classes("primary-btn", "photo-use-row"); if (busy) disabled(); onClick {
                            onSelectGame?.invoke(d.frames, d.expected.toIntOrNull())
                            open = false; drafts = emptyList(); sources = emptyList(); points = emptyList()
                        } }) { Text("Použít tento zápis") }
                    } else {
                    Div(attrs = { classes("training-meta") }) {
                        Label {
                            Text("Hráč")
                            Select(attrs = { classes("field"); onChange { change(d.copy(player = it.value ?: "", playerOverride = true)) } }) {
                                Option("", attrs = { if (d.player.isEmpty()) selected() }) { Text("— vyber hráče —") }
                                profiles.forEach { p -> Option(p.blPlayerId, attrs = { if (d.player == p.blPlayerId) selected() }) { Text(p.displayName) } }
                            }
                        }
                        Label { Text("Číslo hry"); Input(type = InputType.Number) { classes("field"); attr("min", "1"); value(d.number); onInput { change(d.copy(number = it.value?.toInt() ?: 0)) } } }
                        Label { Text("Kontrolní skóre z fotky"); Input(type = InputType.Text) { classes("field"); attr("inputmode", "numeric"); value(d.expected); onInput { change(d.copy(expected = it.value)) } } }
                    }
                    if (training && d.playerOverride) Button(attrs = {
                        classes("small-btn"); onClick { change(d.copy(player = positionPlayers[d.position] ?: "", playerOverride = false)) }
                    }) { Text("Použít hráče podle pozice v sérii") }
                    ScoresheetFields(type, d.frames, onChange = { frames, _ -> change(d.copy(frames = frames)) }, highlightIncomplete = true)
                    P { Text("Součet hodů: $total · OCR: ${d.raw.joinToString(" | ") { it.ifBlank { "?" } }}") }
                    P(attrs = { classes("muted") }) { Text("Hody můžeš opravit přímo v políčkách výše. Zkontroluj i splity; OCR je automaticky neoznačuje. Přidej příponu s.") }
                    photoFramesError(type, d.frames)?.let { P { Text(it) } }
                    if (d.expected.isNotBlank() && d.expected.toIntOrNull() != total) P { Text("Kontrolní skóre nesouhlasí se součtem hodů.") }
                    if (allowedGames != null && "${d.player}#${d.number}" !in allowedGames) P { Text("Vyber existující hru hráče v tomto zápase.") }
                    allowedGames?.get("${d.player}#${d.number}")?.let { league -> if (league != total) P { Text("Ligové skóre $league nesouhlasí se zápisem.") } }
                    if (sheets.containsKey("${d.player}#${d.number}") || !scores?.get("${d.player}#${d.number}").isNullOrBlank()) P { Text("Existující výsledek této hry bude nahrazen.") }
                    }
                }
                }
            }
            Div(attrs = {
                classes(*(listOfNotNull("photo-upload-zone", "photo-upload-dragging".takeIf { dragging }).toTypedArray()))
                onDragOver { ev -> ev.preventDefault(); if (!busy) dragging = true }
                onDragLeave { ev ->
                    val related = ev.nativeEvent.asDynamic().relatedTarget
                    if (related == null || !ev.currentTarget.asDynamic().contains(related) as Boolean) dragging = false
                }
                onDrop { ev ->
                    ev.preventDefault(); ev.stopPropagation(); dragging = false
                    val files = ev.nativeEvent.asDynamic().dataTransfer?.files as? FileList
                    loadFiles((0 until (files?.length ?: 0)).mapNotNull { files?.get(it) })
                }
            }) {
                H4 { Text(if (singleGame) "Přetáhni sem fotku výsledkové tabule" else "Přetáhni sem fotky výsledkové tabule") }
                P(attrs = { classes("muted") }) { Text(if (singleGame) "JPG, PNG, WebP nebo HEIC/HEIF · do 8 MB" else "JPG, PNG, WebP nebo HEIC/HEIF · nejvýše 12 fotek po 8 MB") }
                Button(attrs = { classes("primary-btn", "photo-upload-button"); if (busy) disabled(); onClick { chooseFiles() } }) {
                    Text(if (singleGame) "Vybrat fotku" else "Vybrat fotky")
                }
            }
            sources.getOrNull(sourceIndex)?.let { source ->
                val rows = photoRows[source.id] ?: defaultRows
                LaunchedEffect(source.id, rows, detectionAttempt) {
                    detecting = true; points = emptyList(); selectionNote = "Hledám tabulku…"
                    try {
                        val result = (window.asDynamic().SgPhotoOcr.detect(source.data, rows, photoRows[source.id] == null) as Promise<dynamic>).await()
                        if (result != null) {
                            val detectedRows = (result.count as? Number)?.toInt()?.takeIf { it in 1..3 }
                            if (detectedRows != null && detectedRows != rows) photoRows[source.id] = detectedRows
                            points = (0 until 4).map { PhotoPoint(result.points[it].x as Double, result.points[it].y as Double) }
                            selectionNote = "Tabulka nalezena automaticky. Můžeš rovnou rozpoznat hráče, nebo výběr opravit."
                        } else selectionNote = "Tabulku se nepodařilo spolehlivě najít. Označ její čtyři rohy ručně."
                    } catch (e: kotlinx.coroutines.CancellationException) { throw e }
                    catch (e: Throwable) { selectionNote = "Automatický výběr není dostupný. Označ čtyři rohy ručně." }
                    finally { detecting = false }
                }
                Div(attrs = { classes("training-meta", "photo-source-controls") }) {
                    Label {
                        Text("Fotka")
                        Select(attrs = { classes("field", "photo-source-select"); if (busy) disabled(); onChange { sourceIndex = it.value?.toIntOrNull() ?: 0; points = emptyList() } }) {
                            sources.forEachIndexed { i, s ->
                                val loadedGames = drafts.count { it.sourceId == s.id }
                                Option("$i", attrs = { if (sourceIndex == i) selected() }) {
                                    Text("${if (loadedGames > 0) "✓ Načteno ($loadedGames) · " else ""}${if (singleGame) "" else "Hra ${s.number} · "}${s.name}")
                                }
                            }
                        }
                    }
                    Label {
                        Text("Počet hráčů na fotce")
                        Select(attrs = { classes("field", "photo-row-count"); if (busy) disabled(); onChange {
                            defaultRows = it.value?.toIntOrNull() ?: 2
                            photoRows[source.id] = defaultRows
                        } }) {
                            for (count in 1..3) Option("$count", attrs = { if (rows == count) selected() }) { Text("$count") }
                        }
                    }
                    if (!singleGame) Label {
                        Text("Číslo hry na fotce")
                        Input(type = InputType.Number) {
                            classes("field"); attr("min", "1"); value(source.number); if (busy) disabled()
                            onInput { ev ->
                                val number = ev.value?.toInt() ?: 0
                                sources = sources.map { if (it.id == source.id) it.copy(number = number) else it }
                                drafts = drafts.map { if (it.sourceId == source.id) it.copy(number = number) else it }
                                confirmed = false
                            }
                        }
                    }
                }
                P { Text(selectionNote) }
                P(attrs = { classes("muted") }) { Text("Při ruční opravě označ vnější rohy framů 1–10 všech $rows hráčů: levý horní → pravý horní → pravý dolní → levý dolní. Zahrň hody a průběžné součty, vynech jména, záhlaví a Tot.") }
                Div(attrs = { classes("photo-picker") }) {
                    Img(src = source.data, alt = "Fotka pro výběr řádku", attrs = {
                        onClick { ev ->
                            if (!busy && !detecting && points.size < 4) {
                                val rect = (ev.nativeEvent.target as Element).getBoundingClientRect()
                                val mouse = ev.nativeEvent as MouseEvent
                                points = points + PhotoPoint((mouse.clientX - rect.left) / rect.width, (mouse.clientY - rect.top) / rect.height)
                            }
                        }
                    })
                    if (points.size == 4) Div(attrs = {
                        classes("photo-selection")
                        val polygon = points.joinToString(",") { "${it.x * 100}% ${it.y * 100}%" }
                        attr("style", "clip-path:polygon($polygon)")
                    }) {}
                    points.forEachIndexed { i, p -> Span(attrs = { classes("photo-point"); attr("style", "left:${p.x * 100}%;top:${p.y * 100}%") }) { Text("${i + 1}") } }
                }
                Div(attrs = { classes("form-actions") }) {
                    Button(attrs = { classes("small-btn"); if (busy || detecting) disabled(); onClick { points = emptyList(); selectionNote = "Označ čtyři rohy tabulky ručně." } }) { Text("Vybrat rohy ručně (${points.size}/4)") }
                    Button(attrs = { classes("small-btn"); if (busy || detecting) disabled(); onClick { detectionAttempt++ } }) { Text("Najít tabulku automaticky") }
                    Button(attrs = { classes("primary-btn"); if (busy || detecting || points.size != 4) disabled(); onClick {
                        if (drafts.any { it.sourceId == source.id } && !window.confirm("Znovu rozpoznat tuto fotku? Ruční opravy jejích hodů budou nahrazeny.")) return@onClick
                        photoRows[source.id] = rows
                        busy = true; error = null; progress = "Spouštím OCR…"; confirmed = false
                        scope.launch {
                            try {
                                val engine = window.asDynamic().SgPhotoOcr
                                val coordinates = points.map { p ->
                                    val point: dynamic = js("({})")
                                    point.x = p.x; point.y = p.y
                                    point
                                }.toTypedArray()
                                val results = (engine.recognizeRows(source.data, coordinates, rows,
                                    { row: Int, n: Int -> progress = "Rozpoznávám hráče $row/$rows, frame $n/10…" },
                                    { text: String, i: Int -> parsePhotoFrame(text, i == 9) != null }) as Promise<dynamic>).await()
                                val recognized = (0 until rows).map { row ->
                                    val result = results[row]
                                    val position = row + 1
                                    val previous = drafts.find { it.sourceId == source.id && it.position == position }
                                    val raw = (0 until 10).map { result.cells[it].text as String }
                                    val frames = raw.mapIndexed { i, text -> FrameDto(parsePhotoFrame(text, i == 9) ?: emptyList()) }
                                    PhotoDraft(source.id, position, nextRevision++, source.name, result.preview as String,
                                        frames, raw, if (previous?.playerOverride == true) previous.player else positionPlayers[position] ?: "",
                                        previous?.number ?: source.number, selected = previous?.selected ?: true,
                                        expected = result.total as String, playerOverride = previous?.playerOverride ?: false)
                                }
                                drafts = (drafts.filterNot { it.sourceId == source.id } + recognized).sortedWith(compareBy({ it.sourceId }, { it.position }))
                                reviewSourceId = source.id
                                points = emptyList()
                                nextUnprocessedPhoto(sourceIndex, sources.map { it.id }, drafts.map { it.sourceId }.toSet())?.let {
                                    sourceIndex = it
                                }
                            } catch (e: Throwable) { error = "OCR selhalo: ${e.message}" }
                            finally { busy = false; progress = "" }
                        }
                    } }) { Text("Rozpoznat hráče na fotce ($rows)") }
                }
            }
            if (busy) P { Text(progress.ifBlank { "Načítám fotky…" }) }
            if (!singleGame && duplicate) P { Text("Více vybraných řádků míří na stejnou hru. Uprav hráče nebo číslo hry.") }
            if (!singleGame && chosen.isNotEmpty()) Label {
                Input(type = InputType.Checkbox) { checked(confirmed); onChange { confirmed = it.value } }
                Text("Zkontroloval/a jsem hody, splity, skóre i přiřazení. Souhlasím s nahrazením případných existujících výsledků vybraných her.")
            }
            error?.let { P(attrs = { classes("form-note") }) { Text(it) } }
            Div(attrs = { classes("form-actions") }) {
                if (!singleGame) Button(attrs = { classes("primary-btn"); if (busy || chosen.isEmpty() || invalid || duplicate || !confirmed) disabled(); onClick {
                    chosen.forEach { d ->
                        val key = "${d.player}#${d.number}"
                        sheets[key] = d.frames; scores?.set(key, Scoring.total(typeForGame(d.number), d.frames.map { it.t }).toString())
                    }
                    onGames(chosen.maxOf { it.number }); drafts = emptyList(); sources = emptyList(); points = emptyList(); positionPlayers.clear(); photoRows.clear(); confirmed = false; open = false
                } }) { Text("Převzít vybrané hry (${chosen.size})") }
                Button(attrs = { classes("small-btn"); if (busy) disabled(); onClick { open = false } }) { Text("Zavřít") }
            }
            P(attrs = { classes("muted") }) { Text(if (singleGame) "Vybraný zápis se otevře k úpravě. Uložíš ho tlačítkem Uložit zápis v dialogu hry." else "Převzaté hry uložíš tlačítkem pro uložení tréninku, turnaje nebo zápisů zápasu.") }
        }
    }
}
